/**
 * Funnel Node Configuration - MA-ARCH-161
 * 
 * Node type styling and configuration for funnel visualization.
 * Extracted for better reuse across builder components.
 */

import { 
  MessageSquare, 
  TextCursor, 
  FileCheck, 
  Calendar 
} from 'lucide-react';

/**
 * Step type color and styling configuration.
 * Each type has a consistent visual language across the builder.
 * IMPORTANT: This must match the original FunnelVisualization.jsx config exactly
 * to maintain visual parity.
 */
export const STEP_TYPE_COLORS = {
  response: {
    label: "Response",
    icon: MessageSquare,
    bgColor: "bg-indigo-500/10",
    bgColorHover: "bg-indigo-500/95",
    borderColor: "border-indigo-500/25",
    borderColorHover: "border-indigo-400/60",
    accentColor: "bg-indigo-500",
    textColor: "text-indigo-300",
    iconColor: "text-indigo-400",
    dotColor: "#6366f1",
  },
  input: {
    label: "Input",
    icon: TextCursor,
    bgColor: "bg-purple-500/10",
    bgColorHover: "bg-purple-500/95",
    borderColor: "border-purple-500/25",
    borderColorHover: "border-purple-400/60",
    accentColor: "bg-purple-500",
    textColor: "text-purple-300",
    iconColor: "text-purple-400",
    dotColor: "#a855f7",
  },
  consent: {
    label: "Consent",
    icon: FileCheck,
    bgColor: "bg-amber-500/10",
    bgColorHover: "bg-amber-500/95",
    borderColor: "border-amber-500/25",
    borderColorHover: "border-amber-400/60",
    accentColor: "bg-amber-500",
    textColor: "text-amber-300",
    iconColor: "text-amber-400",
    dotColor: "#f59e0b",
  },
  booking: {
    label: "Booking",
    icon: Calendar,
    bgColor: "bg-blue-500/10",
    bgColorHover: "bg-blue-500/95",
    borderColor: "border-blue-500/25",
    borderColorHover: "border-blue-400/60",
    accentColor: "bg-blue-500",
    textColor: "text-blue-300",
    iconColor: "text-blue-400",
    dotColor: "#3b82f6",
  },
  // Legacy types - mapped to response
  message: {
    label: "Response",
    icon: MessageSquare,
    bgColor: "bg-indigo-500/10",
    bgColorHover: "bg-indigo-500/95",
    borderColor: "border-indigo-500/25",
    borderColorHover: "border-indigo-400/60",
    accentColor: "bg-indigo-500",
    textColor: "text-indigo-300",
    iconColor: "text-indigo-400",
    dotColor: "#6366f1",
  },
  question: {
    label: "Response",
    icon: MessageSquare,
    bgColor: "bg-indigo-500/10",
    bgColorHover: "bg-indigo-500/95",
    borderColor: "border-indigo-500/25",
    borderColorHover: "border-indigo-400/60",
    accentColor: "bg-indigo-500",
    textColor: "text-indigo-300",
    iconColor: "text-indigo-400",
    dotColor: "#6366f1",
  },
};

/**
 * Get node type configuration with fallback.
 * @param {string} nodeType - The node type
 * @returns {Object} - The configuration object
 */
export function getNodeTypeConfig(nodeType) {
  return STEP_TYPE_COLORS[nodeType] || STEP_TYPE_COLORS.response;
}

/**
 * Get display name for a node.
 * Uses metadata.label if available, otherwise falls back to type label.
 * @param {Object} node - The node object
 * @returns {string} - Display name
 */
export function getNodeDisplayName(node) {
  const config = getNodeTypeConfig(node.node_type);
  if (node.metadata?.label && node.metadata.label.trim() !== '') {
    return node.metadata.label;
  }
  return config.label;
}

/**
 * Check if a label is an internal/temp label that shouldn't be displayed.
 * @param {string} label - The label to check
 * @returns {boolean} - True if internal
 */
export function isInternalLabel(label) {
  if (!label || typeof label !== 'string') return true;
  if (label.trim() === '') return true;
  if (label.startsWith('temp_') || label.startsWith('temp ')) return true;
  if (label.match(/^[a-z0-9]{8,}$/i)) return true;
  if (label.match(/^(temp|__)/i)) return true;
  return false;
}

/**
 * Get a clean display label with fallback.
 * @param {string} label - The raw label
 * @param {string} fallback - Fallback if label is internal
 * @returns {string} - Clean label
 */
export function getCleanLabel(label, fallback) {
  if (isInternalLabel(label)) return fallback;
  return label;
}

/**
 * Parse a condition string to a human-readable label.
 * @param {string} condition - The condition string
 * @returns {string|null} - Human-readable label or null
 */
export function parseConditionToLabel(condition) {
  if (!condition) return null;
  if (isInternalLabel(condition)) return null;
  
  const valueMatch = condition.match(/[='"]+\s*['"]?([^'"]+)['"]?\s*$/);
  if (valueMatch) {
    const value = valueMatch[1];
    if (isInternalLabel(value)) return null;
    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  
  const simpleMatch = condition.match(/^[a-zA-Z_]+$/);
  if (simpleMatch) {
    return condition.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  
  const cleaned = condition
    .replace(/response[._]?value\s*[=!<>]+\s*/gi, '')
    .replace(/['"]/g, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
  
  if (isInternalLabel(cleaned) || !cleaned) return null;
  return cleaned;
}

export default {
  STEP_TYPE_COLORS,
  getNodeTypeConfig,
  getNodeDisplayName,
  isInternalLabel,
  getCleanLabel,
  parseConditionToLabel,
};
